import { useEffect, useState } from "react";
import { useSettings } from "@/context/SettingsContext";
import api from "@/utils/api";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  DollarSign, 
  Plus,
  TrendingUp,
  Award,
  Mail,
  Phone,
  Edit2,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

const API_BASE = "/api";

const TrainerDetail = () => {
  const { trainerId } = useParams();
  const navigate = useNavigate();
  const { formatDate, formatCurrency } = useSettings();
  const [trainer, setTrainer] = useState(null);
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [unpaidSessions, setUnpaidSessions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSessionDialogOpen, setIsSessionDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [calculatedAmount, setCalculatedAmount] = useState(0);
  const [sessionFormData, setSessionFormData] = useState({
    session_date: new Date().toISOString().split('T')[0],
    session_type: "",
    duration_hours: "",
    notes: "",
  });
  const [paymentFormData, setPaymentFormData] = useState({
    payment_period: `${new Date().toLocaleString('default', { month: 'long' })} ${new Date().getFullYear()}`,
    payment_method: "bank_transfer",
    notes: "",
  });

  useEffect(() => {
    fetchTrainerData();
  }, [trainerId]);

  useEffect(() => {
    if (isPaymentDialogOpen) {
      fetchUnpaidSessions();
    }
  }, [isPaymentDialogOpen]);

  useEffect(() => {
    calculatePaymentAmount();
  }, [selectedSessions, trainer]);

  const fetchTrainerData = async () => {
    try {
      const [trainerRes, statsRes, sessionsRes, paymentsRes] = await Promise.all([
        api.get(`/trainers/${trainerId}`),
        api.get(`/trainers/${trainerId}/stats`),
        api.get(`/trainer-sessions/trainer/${trainerId}`),
        api.get(`/trainer-payments/trainer/${trainerId}`),
      ]);
      setTrainer(trainerRes.data);
      setStats(statsRes.data);
      setSessions(sessionsRes.data);
      setPayments(paymentsRes.data);
    } catch (error) {
      toast.error("Failed to load trainer data");
      navigate("/trainers");
    } finally {
      setLoading(false);
    }
  };

  const fetchUnpaidSessions = async () => {
    try {
      const response = await api.get(`/trainer-sessions/trainer/${trainerId}/unpaid`);
      setUnpaidSessions(response.data);
      // Auto-select all unpaid sessions by default
      setSelectedSessions(response.data.map(s => s.id));
    } catch (error) {
      toast.error("Failed to load unpaid sessions");
    }
  };

  const calculatePaymentAmount = () => {
    if (!trainer || selectedSessions.length === 0) {
      setCalculatedAmount(0);
      return;
    }

    const selected = unpaidSessions.filter(s => selectedSessions.includes(s.id));
    const { payment_type, payment_rate } = trainer;

    let amount = 0;
    if (payment_type === "per_class") {
      amount = selected.length * payment_rate;
    } else if (payment_type === "daily") {
      const uniqueDays = new Set(selected.map(s => s.session_date));
      amount = uniqueDays.size * payment_rate;
    } else {
      amount = payment_rate;
    }

    setCalculatedAmount(amount);
  };

  const handleSessionSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSession) {
        await api.patch(`/trainer-sessions/${editingSession.id}`, {
          ...sessionFormData,
          duration_hours: parseFloat(sessionFormData.duration_hours),
        });
        toast.success("Session updated successfully");
      } else {
        await api.post(`/trainer-sessions`, {
          trainer_id: trainerId,
          ...sessionFormData,
          duration_hours: parseFloat(sessionFormData.duration_hours),
        });
        toast.success("Session recorded successfully");
      }
      fetchTrainerData();
      setIsSessionDialogOpen(false);
      resetSessionForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save session");
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (selectedSessions.length === 0) {
      toast.error("Please select at least one session");
      return;
    }
    try {
      await api.post(`/trainer-payments`, {
        trainer_id: trainerId,
        ...paymentFormData,
        session_ids: selectedSessions,
      });
      toast.success("Payment processed successfully");
      fetchTrainerData();
      setIsPaymentDialogOpen(false);
      resetPaymentForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    }
  };

  const handleEditSession = (session) => {
    setEditingSession(session);
    setSessionFormData({
      session_date: session.session_date,
      session_type: session.session_type,
      duration_hours: session.duration_hours.toString(),
      notes: session.notes || "",
    });
    setIsSessionDialogOpen(true);
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm("Are you sure you want to delete this session?")) return;
    try {
      await api.delete(`/trainer-sessions/${sessionId}`);
      toast.success("Session deleted successfully");
      fetchTrainerData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete session");
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm("Are you sure? This will mark all related sessions as unpaid.")) return;
    try {
      await api.delete(`/trainer-payments/${paymentId}`);
      toast.success("Payment deleted and sessions marked as unpaid");
      fetchTrainerData();
    } catch (error) {
      toast.error("Failed to delete payment");
    }
  };

  const toggleSessionSelection = (sessionId) => {
    setSelectedSessions(prev => 
      prev.includes(sessionId)
        ? prev.filter(id => id !== sessionId)
        : [...prev, sessionId]
    );
  };

  const resetSessionForm = () => {
    setSessionFormData({
      session_date: new Date().toISOString().split('T')[0],
      session_type: "",
      duration_hours: "",
      notes: "",
    });
    setEditingSession(null);
  };

  const resetPaymentForm = () => {
    setPaymentFormData({
      payment_period: `${new Date().toLocaleString('default', { month: 'long' })} ${new Date().getFullYear()}`,
      payment_method: "bank_transfer",
      notes: "",
    });
    setSelectedSessions([]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lime-400"></div>
      </div>
    );
  }

  if (!trainer) {
    return null;
  }

  return (
    <div data-testid="trainer-detail-page">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate("/trainers")}
          className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-4 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Trainers</span>
        </button>
        
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-black tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {trainer.name}
            </h1>
            <div className="flex flex-col gap-2 text-zinc-600">
              <div className="flex items-center gap-2">
                <Award size={16} />
                <span>{trainer.specialization}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail size={16} />
                <span>{trainer.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone size={16} />
                <span>{trainer.phone}</span>
              </div>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Dialog open={isSessionDialogOpen} onOpenChange={(open) => {
              setIsSessionDialogOpen(open);
              if (!open) resetSessionForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold">
                  <Plus size={20} className="mr-2" />
                  Record Session
                </Button>
              </DialogTrigger>
              <DialogContent aria-describedby="session-dialog-description">
                <DialogHeader>
                  <DialogTitle>{editingSession ? "Edit" : "Record"} Training Session</DialogTitle>
                </DialogHeader>
                <p id="session-dialog-description" className="sr-only">
                  {editingSession ? "Edit training session" : "Record a new training session"}
                </p>
                <form onSubmit={handleSessionSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="session_date">Date</Label>
                    <input
                      type="date"
                      id="session_date"
                      value={sessionFormData.session_date}
                      onChange={(e) => setSessionFormData({ ...sessionFormData, session_date: e.target.value })}
                      className="w-full h-11 px-3 bg-zinc-50 border border-zinc-200 rounded-md focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400 transition-all"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="session_type">Session Type</Label>
                    <Input
                      id="session_type"
                      placeholder="e.g., Yoga Class, Personal Training"
                      value={sessionFormData.session_type}
                      onChange={(e) => setSessionFormData({ ...sessionFormData, session_type: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="duration_hours">Duration (hours)</Label>
                    <Input
                      id="duration_hours"
                      type="number"
                      step="0.5"
                      min="0.5"
                      max="24"
                      placeholder="e.g., 1.5"
                      value={sessionFormData.duration_hours}
                      onChange={(e) => setSessionFormData({ ...sessionFormData, duration_hours: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="notes">Notes (Optional)</Label>
                    <Textarea
                      id="notes"
                      placeholder="Additional notes"
                      value={sessionFormData.notes}
                      onChange={(e) => setSessionFormData({ ...sessionFormData, notes: e.target.value })}
                      rows={3}
                    />
                  </div>
                  <Button type="submit" className="w-full bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold">
                    {editingSession ? "Update" : "Record"} Session
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={isPaymentDialogOpen} onOpenChange={(open) => {
              setIsPaymentDialogOpen(open);
              if (!open) resetPaymentForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-blue-500 text-white hover:bg-blue-600 font-bold">
                  <DollarSign size={20} className="mr-2" />
                  Process Payment
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby="payment-dialog-description">
                <DialogHeader>
                  <DialogTitle>Process Trainer Payment</DialogTitle>
                </DialogHeader>
                <p id="payment-dialog-description" className="sr-only">
                  Select sessions and process payment
                </p>
                <form onSubmit={handlePaymentSubmit} className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                    <h3 className="font-bold text-blue-900 mb-3">Select Sessions to Pay</h3>
                    {unpaidSessions.length === 0 ? (
                      <p className="text-sm text-zinc-500">No unpaid sessions available</p>
                    ) : (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {unpaidSessions.map((session) => (
                          <div key={session.id} className="flex items-start gap-3 p-3 bg-white rounded border border-zinc-200">
                            <Checkbox
                              checked={selectedSessions.includes(session.id)}
                              onCheckedChange={() => toggleSessionSelection(session.id)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium text-zinc-900">{session.session_type}</p>
                                  <p className="text-xs text-zinc-500">
                                    {formatDate(session.session_date)} • {session.duration_hours}h
                                  </p>
                                </div>
                                <span className="text-sm font-bold text-blue-600">
                                  {formatCurrency((trainer.payment_type === 'per_class' ? trainer.payment_rate : 0))}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="bg-lime-50 border border-lime-300 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-zinc-900">Calculated Payment Amount:</span>
                      <span className="text-3xl font-black text-lime-700">{formatCurrency(calculatedAmount)}</span>
                    </div>
                    <p className="text-xs text-zinc-600 mt-2">
                      {selectedSessions.length} session(s) selected • {trainer.payment_type.replace('_', ' ')} @ {formatCurrency(trainer.payment_rate)}
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="payment_period">Payment Period</Label>
                    <Input
                      id="payment_period"
                      placeholder="e.g., December 2025"
                      value={paymentFormData.payment_period}
                      onChange={(e) => setPaymentFormData({ ...paymentFormData, payment_period: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="payment_method">Payment Method</Label>
                    <select
                      id="payment_method"
                      value={paymentFormData.payment_method}
                      onChange={(e) => setPaymentFormData({ ...paymentFormData, payment_method: e.target.value })}
                      className="w-full h-11 px-3 bg-zinc-50 border border-zinc-200 rounded-md focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400 transition-all"
                    >
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="cash">Cash</option>
                      <option value="check">Check</option>
                      <option value="upi">UPI</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="payment_notes">Notes (Optional)</Label>
                    <Textarea
                      id="payment_notes"
                      placeholder="Additional payment notes"
                      value={paymentFormData.notes}
                      onChange={(e) => setPaymentFormData({ ...paymentFormData, notes: e.target.value })}
                      rows={3}
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-blue-500 text-white hover:bg-blue-600 font-bold"
                    disabled={selectedSessions.length === 0}
                  >
                    Process Payment - {formatCurrency(calculatedAmount)}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-zinc-500 uppercase tracking-wider">Total Sessions</p>
            <Calendar className="text-blue-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-zinc-900">{stats.total_sessions}</p>
        </div>

        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-zinc-500 uppercase tracking-wider">Total Hours</p>
            <Clock className="text-orange-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-zinc-900">{stats.total_hours}h</p>
        </div>

        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-zinc-500 uppercase tracking-wider">Total Paid</p>
            <DollarSign className="text-green-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-zinc-900">{formatCurrency(stats.total_earnings)}</p>
        </div>

        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm border-orange-300 bg-orange-50">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-orange-700 uppercase tracking-wider font-bold">Pending Payment</p>
            <DollarSign className="text-orange-600" size={24} />
          </div>
          <p className="text-3xl font-bold text-orange-700">{formatCurrency(stats.pending_amount)}</p>
          <p className="text-xs text-orange-600 mt-1">Awaiting payment</p>
        </div>

        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-zinc-500 uppercase tracking-wider">This Month</p>
            <TrendingUp className="text-lime-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-zinc-900">{formatCurrency(stats.current_month_earnings)}</p>
          <p className="text-xs text-zinc-500 mt-1">
            {stats.current_month_sessions} sessions • {stats.current_month_hours}h
          </p>
          {stats.current_month_pending > 0 && (
            <p className="text-xs text-orange-600 mt-1 font-medium">
              {formatCurrency(stats.current_month_pending)} pending
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Work History */}
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-zinc-200">
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Work History
            </h2>
          </div>
          <div className="overflow-y-auto max-h-96">
            {sessions.length === 0 ? (
              <p className="px-6 py-12 text-center text-zinc-500">
                No sessions recorded yet
              </p>
            ) : (
              <div className="divide-y divide-zinc-100">
                {sessions.map((session) => (
                  <div key={session.id} className={`px-6 py-4 hover:bg-zinc-50 transition-colors ${
                    !session.paid ? 'border-l-4 border-l-orange-400' : ''
                  }`}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h3 className="font-medium text-zinc-900">{session.session_type}</h3>
                        {!session.paid ? (
                          <span className="text-xs text-orange-600 bg-orange-100 px-2 py-0.5 rounded font-medium mt-1 inline-block">
                            UNPAID
                          </span>
                        ) : (
                          <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded font-medium mt-1 inline-block">
                            ✓ PAID
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-500 bg-zinc-100 px-2 py-1 rounded">
                          {session.duration_hours}h
                        </span>
                        {!session.paid && (
                          <>
                            <button
                              onClick={() => handleEditSession(session)}
                              className="p-1.5 hover:bg-zinc-100 rounded transition-colors"
                              title="Edit"
                            >
                              <Edit2 size={14} className="text-zinc-600" />
                            </button>
                            <button
                              onClick={() => handleDeleteSession(session.id)}
                              className="p-1.5 hover:bg-red-50 rounded transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={14} className="text-red-600" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-zinc-600">
                      <Calendar size={14} />
                      <span>{formatDate(session.session_date)}</span>
                    </div>
                    {session.notes && (
                      <p className="text-sm text-zinc-500 mt-2">{session.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Payment History */}
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-zinc-200">
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Payment History
            </h2>
          </div>
          <div className="overflow-y-auto max-h-96">
            {payments.length === 0 ? (
              <p className="px-6 py-12 text-center text-zinc-500">
                No payments recorded yet
              </p>
            ) : (
              <div className="divide-y divide-zinc-100">
                {payments.map((payment) => (
                  <div key={payment.id} className="px-6 py-4 hover:bg-zinc-50 transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-zinc-900">{payment.payment_period}</h3>
                        <p className="text-xs text-zinc-500 mt-1">
                          {payment.sessions_count} sessions • {payment.total_hours}h
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <p className="text-lg font-bold text-green-600">{formatCurrency(payment.amount)}</p>
                          <p className="text-xs text-zinc-500 capitalize">{payment.payment_method.replace('_', ' ')}</p>
                        </div>
                        <button
                          onClick={() => handleDeletePayment(payment.id)}
                          className="p-1.5 hover:bg-red-50 rounded transition-colors"
                          title="Delete Payment"
                        >
                          <Trash2 size={14} className="text-red-600" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-zinc-600 mt-2">
                      <Calendar size={14} />
                      <span>{formatDate(payment.payment_date)}</span>
                    </div>
                    {payment.notes && (
                      <p className="text-sm text-zinc-500 mt-2">{payment.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainerDetail;
